// T9: LC / back-to-back LC / amendment / checklist interfaces for the commercial module.
// Field names mirror the backend records under
// backend/src/main/java/com/kervolt/oms/commercial/dataclass/ 1:1 (LC_VIEW/LC_MANAGE surface only —
// UD/EXP/CashIncentiveClaim/CommercialReport shapes belong to other tasks).

export type LcStatus = 'RECEIVED' | 'PARTIALLY_SHIPPED' | 'FULLY_SHIPPED' | 'REALIZED' | 'CLOSED';
export type B2bLcStatus = 'OPENED' | 'PARTIALLY_UTILIZED' | 'FULLY_UTILIZED' | 'CLOSED';
export type DueStatus = 'OK' | 'DUE_SOON' | 'EXPIRED';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const LC_STATUS_COLORS: Record<LcStatus, ChipColor> = {
    RECEIVED: 'default',
    PARTIALLY_SHIPPED: 'info',
    FULLY_SHIPPED: 'primary',
    REALIZED: 'success',
    CLOSED: 'default',
};

export const B2B_STATUS_COLORS: Record<B2bLcStatus, ChipColor> = {
    OPENED: 'default',
    PARTIALLY_UTILIZED: 'info',
    FULLY_UTILIZED: 'primary',
    CLOSED: 'default',
};

export const DUE_STATUS_COLORS: Record<DueStatus, ChipColor> = {
    OK: 'success',
    DUE_SOON: 'warning',
    EXPIRED: 'error',
};

export interface MismatchedCurrencyAmount {
    currencyCode: string;
    amount: number;
}

/** Paginated LC register row (backend ExportLcListItemDTO). */
export interface ExportLcListItem {
    id: string;
    lcNumber: string;
    issuingBank: string;
    buyerName: string;
    currencyCode: string;
    lcAmount: number;
    effectiveMaxValue: number;
    status: LcStatus;
    expiryDate: string;
    latestShipmentDate?: string;
    amended: boolean;
    dueStatus: DueStatus;
    dueReason?: string;
}

/** LC detail (backend ExportLcDTO). issuingBankPartyId/advisingBankPartyId link the free-text
 * bank name fields to the commercial party master (Task 4a, spec §2, 7.4) -- nullable soft
 * references, set only via the CommercialPartyAutocomplete pickers on ExportLcForm. */
export interface ExportLc {
    id: string;
    institutionId: string;
    lcNumber: string;
    issuingBank: string;
    advisingBank?: string;
    issuingBankPartyId?: string;
    advisingBankPartyId?: string;
    buyerId?: string;
    buyerName: string;
    salesOrderId?: string;
    salesOrderRef?: string;
    currencyCode: string;
    lcAmount: number;
    tolerancePercent: number;
    b2bEntitlementPercent?: number;
    effectiveMaxValue: number;
    issueDate: string;
    expiryDate: string;
    latestShipmentDate?: string;
    status: LcStatus;
    realizedAt?: string;
    notes?: string;
    amendmentCount: number;
    amended: boolean;
    dueStatus: DueStatus;
    dueReason?: string;
}

/** §4 soft-reference rule: buyer/order are a required name + optional raw UUID, no picker until T-COMM-1. */
export interface CreateExportLcRequest {
    lcNumber: string;
    issuingBank: string;
    advisingBank?: string;
    issuingBankPartyId?: string;
    advisingBankPartyId?: string;
    buyerId?: string;
    buyerName: string;
    salesOrderId?: string;
    salesOrderRef?: string;
    currencyCode: string;
    lcAmount: number;
    tolerancePercent: number;
    b2bEntitlementPercent?: number;
    issueDate: string;
    expiryDate: string;
    latestShipmentDate?: string;
    notes?: string;
}

/** currencyCode IS present (R-28) — the backend refuses any change, it is not silently dropped. */
export interface UpdateExportLcRequest extends CreateExportLcRequest {}

export interface UpdateLcStatusRequest {
    status: LcStatus;
}

/** R-6: append-only. No update/delete request shape exists on purpose. */
export interface LcAmendment {
    id: string;
    exportLcId: string;
    amendmentNumber: string;
    amendmentDate: string;
    description?: string;
    newLcAmount?: number;
    newExpiryDate?: string;
    newLatestShipmentDate?: string;
    previousLcAmount?: number;
    previousExpiryDate?: string;
    previousLatestShipmentDate?: string;
}

export interface CreateLcAmendmentRequest {
    amendmentDate: string;
    description?: string;
    newLcAmount?: number;
    newExpiryDate?: string;
    newLatestShipmentDate?: string;
}

export interface LcChecklistItem {
    id: string;
    exportLcId: string;
    documentName: string;
    required: boolean;
    submitted: boolean;
    submittedDate?: string;
    remarks?: string;
}

export interface CreateLcChecklistItemRequest {
    documentName: string;
    required: boolean;
    remarks?: string;
}

export interface UpdateLcChecklistItemRequest {
    required: boolean;
    submitted: boolean;
    submittedDate?: string;
    remarks?: string;
}

/** Back-to-back LC detail (backend BackToBackLcDTO). */
export interface BackToBackLc {
    id: string;
    institutionId: string;
    masterLcId: string;
    b2bLcNumber: string;
    issuingBank: string;
    supplierId?: string;
    supplierName: string;
    currencyCode: string;
    amount: number;
    issueDate: string;
    expiryDate: string;
    latestShipmentDate?: string;
    status: B2bLcStatus;
    utilizedAmount: number;
    remainingHeadroom: number;
}

/** R-26: supplierId is a validated reference when present; supplierName is required either way. */
export interface CreateBackToBackLcRequest {
    b2bLcNumber: string;
    issuingBank: string;
    supplierId?: string;
    supplierName: string;
    currencyCode: string;
    amount: number;
    issueDate: string;
    expiryDate: string;
    latestShipmentDate?: string;
}

/** masterLcId and currencyCode ARE present (R-28) — changing either is refused server-side. */
export interface UpdateBackToBackLcRequest {
    masterLcId: string;
    b2bLcNumber: string;
    issuingBank: string;
    supplierId?: string;
    supplierName: string;
    currencyCode: string;
    amount: number;
    issueDate: string;
    expiryDate: string;
    latestShipmentDate?: string;
    /** null/omitted = leave unchanged; the only other legal value is 'CLOSED' (manual transition). */
    status?: B2bLcStatus | null;
}

export interface B2bUtilization {
    id: string;
    backToBackLcId: string;
    utilizationDate: string;
    amount: number;
    reference?: string;
    remarks?: string;
}

export interface CreateB2bUtilizationRequest {
    utilizationDate: string;
    amount: number;
    reference?: string;
    remarks?: string;
}

export interface CommercialSettings {
    institutionId: string;
    defaultCurrencyCode: string;
    repatriationWindowDays: number;
    lcExpiryWarningDays: number;
    shipmentDateWarningDays: number;
    udValidityWarningDays: number;
    defaultB2bEntitlementPercent: number;
    cashIncentiveEnabled: boolean;
    defaultCashIncentiveRatePercent: number;
}

export interface UpdateCommercialSettingsRequest {
    defaultCurrencyCode: string;
    repatriationWindowDays: number;
    lcExpiryWarningDays: number;
    shipmentDateWarningDays: number;
    udValidityWarningDays: number;
    defaultB2bEntitlementPercent: number;
    cashIncentiveEnabled: boolean;
    defaultCashIncentiveRatePercent: number;
}

export interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

/**
 * T10 — UD register / EXP form / cash-incentive claim / commercial settings interfaces.
 * Mirrors the backend DTOs in `com.kervolt.oms.commercial.dataclass` verbatim (R-15). LC/B2B
 * shapes belong to T9's LC screens on a parallel branch and are NOT declared here.
 */

export interface IPage<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}
// DueStatus is declared once above (shared by T9 LC and T10 UD/EXP surfaces).

// ---- UD register --------------------------------------------------------------------------

export type UdStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'CLOSED';

export interface IUdRegister {
    id: string;
    institutionId: string;
    udNumber: string;
    udDate: string;
    exportLcId: string | null;
    salesOrderId: string | null;
    salesOrderRef: string | null;
    bondLicenseId: string | null;
    issuingAuthority: string;
    status: UdStatus;
    approvalDate: string | null;
    validTillDate: string | null;
    lastAmendedDate: string | null;
    remarks: string | null;
    dueStatus: DueStatus;
    dueReason: string | null;
}

export interface ICreateUdRegisterRequest {
    udNumber: string;
    udDate: string;
    exportLcId?: string | null;
    salesOrderId?: string | null;
    salesOrderRef?: string | null;
    bondLicenseId?: string | null;
    issuingAuthority: string;
    validTillDate?: string | null;
    remarks?: string | null;
}

export interface IUpdateUdRegisterRequest extends ICreateUdRegisterRequest {
    approvalDate?: string | null;
    lastAmendedDate?: string | null;
    status?: UdStatus | null;
}

export type MaterialType = 'FABRIC' | 'YARN' | 'TRIMS' | 'ACCESSORIES' | 'PACKING' | 'OTHER';

/** `balanceQty` is the API's own figure (entitledQty - utilizedQty) — display only, NEVER
 * recomputed client-side (brief's binding test). */
export interface IUdEntitlementLine {
    id: string;
    udRegisterId: string;
    materialType: MaterialType;
    description: string | null;
    hsCode: string | null;
    uom: string;
    entitledQty: number;
    unitPrice: number | null;
    currencyCode: string;
    utilizedQty: number;
    balanceQty: number;
}

export interface ICreateUdEntitlementLineRequest {
    materialType: MaterialType;
    description?: string | null;
    hsCode?: string | null;
    uom: string;
    entitledQty: number;
    unitPrice?: number | null;
    currencyCode: string;
}

export type IUpdateUdEntitlementLineRequest = ICreateUdEntitlementLineRequest;

export interface IUdUtilization {
    id: string;
    udEntitlementLineId: string;
    utilizationDate: string;
    qty: number;
    reference: string | null;
    backToBackLcId: string | null;
    remarks: string | null;
}

export interface ICreateUdUtilizationRequest {
    utilizationDate: string;
    qty: number;
    reference?: string | null;
    backToBackLcId?: string | null;
    remarks?: string | null;
}

// ---- EXP form ------------------------------------------------------------------------------

export type ExpRealizationStatus = 'NOT_REALIZED' | 'PARTIALLY_REALIZED' | 'REALIZED' | 'WRITTEN_OFF';

export interface IExpForm {
    id: string;
    institutionId: string;
    expNumber: string;
    expDate: string;
    exportLcId: string | null;
    salesOrderId: string | null;
    salesOrderRef: string | null;
    buyerId: string | null;
    buyerName: string;
    bankName: string;
    bankBranch: string | null;
    currencyCode: string;
    invoiceValue: number;
    shipmentDate: string | null;
    blNumber: string | null;
    realizationStatus: ExpRealizationStatus;
    realizedAmount: number;
    balanceValue: number;
    fddRealizationDate: string | null;
    prcNumber: string | null;
    prcDate: string | null;
    remarks: string | null;
    overdueRealization: boolean;
    daysOutstanding: number | null;
    /** BDT per 1 unit of currencyCode, entered at shipment (null on a legacy EXP). */
    bookingRate: number | null;
    bookedBaseAmount: number | null;
    receivableJournalEntryId: string | null;
    /** Journal of the write-off, when the EXP was written off. */
    writeOffJournalEntryId?: string | null;
    /** True when Dr Export Receivable / Cr Export Sales is booked in the ledger. */
    receivableBooked: boolean;
    /** Only on the response of a write whose ledger posting was skipped or failed. */
    postingWarning: string | null;
}

export interface ICreateExpFormRequest {
    expNumber: string;
    expDate: string;
    exportLcId?: string | null;
    salesOrderId?: string | null;
    salesOrderRef?: string | null;
    buyerId?: string | null;
    buyerName: string;
    bankName: string;
    bankBranch?: string | null;
    currencyCode: string;
    invoiceValue: number;
    shipmentDate?: string | null;
    blNumber?: string | null;
    prcNumber?: string | null;
    prcDate?: string | null;
    remarks?: string | null;
    /** BDT per 1 unit of currencyCode at shipment. Required for a non-BDT EXP; 1 (or omitted) for BDT. */
    bookingRate?: number | null;
}

export interface IBookingRateSuggestion {
    currencyCode: string;
    rate: number | null;
    asOf: string | null;
    source: 'BDT' | 'LAST_BOOKING' | 'LAST_REALIZATION' | 'NONE';
}

export interface IBookExpReceivableRequest {
    bookingRate: number;
    /** Optional posting date (yyyy-MM-dd), e.g. when the shipment period has since closed. */
    postingDate?: string | null;
}

export interface IUpdateExpFormRequest extends ICreateExpFormRequest {
    status?: ExpRealizationStatus | null;
}

/** No `currencyCode` (R-19) — inherits the parent EXP's. `exchangeRate` is REQUIRED always
 * (R-27); the "must be exactly 1 when the EXP's currency is BDT" rule is service-side. */
export interface IExpRealization {
    id: string;
    expFormId: string;
    realizationDate: string;
    amount: number;
    exchangeRate: number;
    /** Bank charges deducted on realization (plan §16.4); server always returns it (defaults to 0). */
    bankCharges: number;
    prcNumber: string | null;
    remarks: string | null;
    journalEntryId: string | null;
    /** BDT receivable cleared at the booked rate (null = legacy posting, no receivable booked). */
    receivableClearedBase: number | null;
    /** Realised FX difference in BDT: positive = gain, negative = loss. */
    fxGainLossBase: number | null;
    postingWarning: string | null;
    /** W3-B2: proceeds distribution rows (empty = not distributed) and the net the bank received. */
    deductions?: IExpRealizationDeduction[];
    netToBank?: number | null;
}

export type ExpDeductionKind = 'SOURCE_TAX' | 'LOAN_LIQUIDATION' | 'ERQ_RETENTION' | 'OTHER';

export interface IExpRealizationDeduction {
    id: string; seq: number; kind: ExpDeductionKind; amount: number; foreignAmount?: number | null;
    accountId?: string | null; bankAccountId?: string | null; drawdownId?: string | null;
    interestAmount?: number | null; chargesAmount?: number | null; principalClearedBase?: number | null;
    fxGainLossBase?: number | null; repaymentId?: string | null; certificateRef?: string | null; memo?: string | null;
}

export interface IExpRealizationDeductionRequest {
    kind: ExpDeductionKind; amount?: number | null; foreignAmount?: number | null; accountId?: string | null;
    bankAccountId?: string | null; drawdownId?: string | null; principalAmount?: number | null;
    interestAmount?: number | null; chargesAmount?: number | null; certificateRef?: string | null; memo?: string | null;
}

export interface IExpRealizationPreview {
    grossForeign: number; exchangeRate: number; grossBase: number; bankCharges: number; deductionsTotal: number;
    netToBank: number; receivableClearedBase?: number | null; fxGainLossBase?: number | null;
    lines: { accountId: string; label: string; debit: number; credit: number }[]; warnings: string[];
}

export interface ICreateExpRealizationRequest {
    realizationDate: string;
    amount: number;
    exchangeRate: number;
    prcNumber?: string | null;
    remarks?: string | null;
    /** Bank charges deducted on realization (plan §16.4). Optional; defaults to 0 server-side. */
    bankCharges?: number | null;
    /** W3-B2: proceeds distribution rows; empty/absent keeps the single-leg posting. */
    deductions?: IExpRealizationDeductionRequest[];
}

export type IUpdateExpRealizationRequest = ICreateExpRealizationRequest;

// ---- Cash incentive claims -----------------------------------------------------------------

export type IncentiveClaimStatus = 'DRAFT' | 'SUBMITTED' | 'UNDER_AUDIT' | 'APPROVED' | 'RECEIVED' | 'REJECTED';

export interface ICashIncentiveClaim {
    id: string;
    institutionId: string;
    claimNumber: string;
    expFormId: string;
    realizedAmountBase: number;
    currencyCode: string;
    realizedAmountBdt: number;
    ratePercent: number;
    claimedAmount: number;
    bankName: string;
    submittedDate: string | null;
    receivedDate: string | null;
    receivedAmount: number | null;
    /** W3-B7: tax deducted at source on the receipt (Dr source-tax account). */
    aitAmount?: number | null;
    status: IncentiveClaimStatus;
    rejectionReason: string | null;
    remarks: string | null;
    approvalJournalEntryId: string | null;
    receiptJournalEntryId: string | null;
    /** True only while a ledger posting was attempted at a transition and failed. */
    ledgerPostingPending: boolean;
    /** Only on the response of a write whose ledger posting was skipped or failed. */
    postingWarning: string | null;
}

/** R-3: when the module flag is off the register comes back EMPTY with a named reason —
 * `enabled === false` always pairs with a non-null `disabledReason` and an empty `claims` page. */
export interface ICashIncentiveRegister {
    claims: IPage<ICashIncentiveClaim>;
    enabled: boolean;
    disabledReason: string | null;
}

export interface ICreateCashIncentiveClaimRequest {
    expFormId: string;
    claimNumber: string;
    bankName: string;
    ratePercent?: number | null;
    remarks?: string | null;
}

export interface IUpdateCashIncentiveClaimRequest {
    claimNumber: string;
    bankName: string;
    remarks?: string | null;
}

export interface IUpdateCashIncentiveClaimStatusRequest {
    status: IncentiveClaimStatus;
    rejectionReason?: string | null;
    receivedDate?: string | null;
    receivedAmount?: number | null;
    aitAmount?: number | null;
}

export interface ICorrectCashIncentiveRateRequest {
    ratePercent: number;
    reason: string;
}

export interface ICorrectCashIncentiveReceiptRequest {
    receivedAmount: number;
    receivedDate: string;
    remarks?: string | null;
    aitAmount?: number | null;
}

// ---- Commercial settings -------------------------------------------------------------------

export type CommissionDirection = 'PAYABLE' | 'RECEIVABLE';

export interface ICommercialSettings {
    institutionId: string;
    defaultCurrencyCode: string;
    repatriationWindowDays: number;
    lcExpiryWarningDays: number;
    shipmentDateWarningDays: number;
    udValidityWarningDays: number;
    defaultB2bEntitlementPercent: number;
    cashIncentiveEnabled: boolean;
    defaultCashIncentiveRatePercent: number;
    // GL-posting account references (plan §16.4, task 2/6) — optional/nullable until an
    // institution configures export-LC GL posting; mirrors UpdateCommercialSettingsRequest.
    exportProceedsAccountId?: string | null;
    bankAccountId?: string | null;
    bankChargesAccountId?: string | null;
    // Printed on export commercial invoices (spec §2 "15.1", plan task 7). Optional/nullable --
    // an institution that never configures this simply falls back to invoice branding's bank
    // block at export-invoice create time. Trailing field, mirrors CommercialSettingsDTO.
    exporterBankDetails?: string | null;
    // Export receivable / realised FX / cash-incentive postings (accounting wave 1). An unset
    // account means the matching posting is skipped with a visible warning. The export SALES
    // (revenue) account is `exportProceedsAccountId`.
    exportReceivableAccountId?: string | null;
    fxGainAccountId?: string | null;
    fxLossAccountId?: string | null;
    cashIncentiveReceivableAccountId?: string | null;
    cashIncentiveIncomeAccountId?: string | null;
    // Unrealised FX revaluation (wave 2): gain must be a REVENUE account, loss an EXPENSE account.
    unrealizedFxGainAccountId?: string | null;
    unrealizedFxLossAccountId?: string | null;
    // B2B LC accounting (accounting wave 2). Clearing null = the procurement GRNI account.
    b2bAcceptanceLiabilityAccountId?: string | null;
    b2bMarginAccountId?: string | null;
    b2bClearingAccountId?: string | null;
    /** W3-B: asset account debited for tax deducted at source (export proceeds, cash incentive). */
    sourceTaxAccountId?: string | null;
    /** Polish 4: BDT bank for cash-incentive receipts; null = bankAccountId while that is a BDT account. */
    cashIncentiveBankAccountId?: string | null;
    /** Buying-house D4: which way agent commission flows. Absent from older responses = PAYABLE. */
    commissionDirection?: CommissionDirection;
}

// ---- Commercial reports (T11) ---------------------------------------------------------------
// Mirrors backend com.kervolt.oms.commercial.dataclass.report records verbatim (R-15). Every
// aggregation is keyed (groupKey, currencyCode) — a mixed-currency institution never produces one
// combined figure at any level (R-2 as extended). "ALL" is the shared grand-total groupKey
// sentinel both report DTOs use.
export const REPORT_GRAND_TOTAL_KEY = 'ALL';

export interface IMismatchedCurrencyAmount {
    currencyCode: string;
    amount: number;
}

/** Backend LcPositionRowDTO. */
export interface ILcPositionRow {
    lcId: string;
    lcNumber: string;
    issuingBank: string;
    buyerName: string;
    currencyCode: string;
    lcAmount: number;
    effectiveMaxValue: number;
    entitlementCap: number;
    b2bOpened: number;
    b2bUtilized: number;
    headroom: number;
    expDeclared: number;
    expRealized: number;
    expUnrealized: number;
    daysToExpiry: number | null;
    daysToLatestShipment: number | null;
    status: LcStatus;
    dueStatus: DueStatus;
    dueReason: string | null;
    unrealizedAgainstClosed: boolean;
    mismatchedCurrencyAmounts: IMismatchedCurrencyAmount[];
}

/** Backend LcPositionReportDTO.Subtotal — one (groupKey, currencyCode) aggregation. */
export interface ILcPositionSubtotal {
    groupKey: string;
    currencyCode: string;
    lcCount: number;
    lcAmount: number;
    effectiveMaxValue: number;
    entitlementCap: number;
    b2bOpened: number;
    b2bUtilized: number;
    headroom: number;
    expDeclared: number;
    expRealized: number;
    expUnrealized: number;
}

/** Backend LcPositionReportDTO. */
export interface ILcPositionReport {
    rows: ILcPositionRow[];
    byBank: ILcPositionSubtotal[];
    grandTotal: ILcPositionSubtotal[];
    truncated: boolean;
    totalMatchingRows: number;
}

/** Backend ExpAgingRowDTO. buyerName grouping is TEXT until T-COMM-1 (§4.7). */
export interface IExpAgingRow {
    expId: string;
    expNumber: string;
    buyerName: string;
    bankName: string;
    currencyCode: string;
    expDate: string;
    invoiceValue: number;
    realizedAmount: number;
    unrealizedAmount: number;
    daysOutstanding: number;
    bucketLabel: string;
}

export type ExpAgingGroupType = 'GRAND' | 'BANK' | 'BUYER';

/** Backend ExpAgingBucketDTO — one aggregation row at the grand/by-bank/by-buyer level. */
export interface IExpAgingBucket {
    groupType: ExpAgingGroupType;
    groupKey: string;
    currencyCode: string;
    bucketLabel: string;
    startDay: number;
    endDayExclusive: number | null;
    count: number;
    amount: number;
}

/** Backend ExpAgingReportDTO. buyerGroupingCaveat (§4.7) is always rendered, never suppressed. */
export interface IExpAgingReport {
    rows: IExpAgingRow[];
    buckets: IExpAgingBucket[];
    buyerGroupingCaveat: string;
    truncated: boolean;
    totalMatchingRows: number;
}

/** Backend ExportReceivableRowDTO: one booked EXP with an open receivable, in FC and BDT. */
export interface IExportReceivableRow {
    expId: string;
    expNumber: string;
    buyerName: string;
    currencyCode: string;
    shipmentDate: string;
    invoiceValue: number;
    realizedAmount: number;
    openForeign: number;
    bookingRate: number;
    bookedBase: number;
    clearedBase: number;
    openBase: number;
    daysOutstanding: number;
    bucketLabel: string;
    realizationStatus: ExpRealizationStatus;
}

export interface IExportReceivableSubtotal {
    key: string;
    count: number;
    openBase: number;
}

/** Backend ExportReceivableReportDTO. */
export interface IExportReceivableReport {
    rows: IExportReceivableRow[];
    byBuyer: IExportReceivableSubtotal[];
    byBucket: IExportReceivableSubtotal[];
    totalOpenBase: number;
    legacyUnbookedCount: number;
    truncated: boolean;
    totalMatchingRows: number;
}

/** Backend IncentiveStatusRowDTO. All amounts BDT (R-27) — no per-currency discipline needed. */
export interface IIncentiveStatusRow {
    claimId: string;
    claimNumber: string;
    expFormId: string;
    status: IncentiveClaimStatus;
    claimedAmount: number;
    receivedAmount: number | null;
    shortfall: number | null;
    submittedDate: string | null;
    daysSubmittedNotReceived: number | null;
}

/** Backend IncentiveStatusReportDTO.StatusSummary. */
export interface IIncentiveStatusSummary {
    status: IncentiveClaimStatus;
    count: number;
    claimedAmount: number;
    receivedAmount: number;
    shortfall: number;
    notYetReceivedAmount: number;
}

/** Backend IncentiveStatusReportDTO.AgingBucket. */
export interface IIncentiveAgingBucket {
    bucketLabel: string;
    startDay: number;
    endDayExclusive: number | null;
    count: number;
    claimedAmount: number;
}

/** Backend IncentiveStatusReportDTO. R-3: enabled === false always pairs with a non-null
 * disabledReason and every list field empty — never a partial report. */
export interface IIncentiveStatusReport {
    rows: IIncentiveStatusRow[];
    byStatus: IIncentiveStatusSummary[];
    agingBuckets: IIncentiveAgingBucket[];
    truncated: boolean;
    totalMatchingRows: number;
    enabled: boolean;
    disabledReason: string | null;
}

export type IUpdateCommercialSettingsRequest = Omit<ICommercialSettings, 'institutionId'>;

// ---- Bond-warehouse license (T8, brief item 13.1) --------------------------------------------
// Mirrors backend BondLicenseDTO/CreateBondLicenseRequest/UpdateBondLicenseRequest verbatim.
// bondType/status are caller-supplied free text on the backend (no enum), so they stay `string`
// here too.

export interface IBondLicense {
    id: string;
    institutionId: string;
    licenseNo: string;
    bondType: string | null;
    issueDate: string | null;
    validTill: string | null;
    status: string;
    remarks: string | null;
}

export interface ICreateBondLicenseRequest {
    licenseNo: string;
    bondType?: string | null;
    issueDate?: string | null;
    validTill?: string | null;
    status: string;
    remarks?: string | null;
}

export type IUpdateBondLicenseRequest = ICreateBondLicenseRequest;

// ---- Bond register report (T8, brief item 13.3) -----------------------------------------------
// Mirrors backend BondRegisterReportDTO/BondRegisterRowDTO verbatim. `materialType`/`uom` are
// null when an item appears only in imports (no BOM line for it on this order); `allowedQty` is
// then 0. `overWastage` (consumedQty > allowedQty) is the backend's own flag -- never recomputed
// client-side. `itemCode`/`itemName` are null only if the item row itself is missing -- render
// "CODE — Name" when both are present, else fall back to `itemId`.

export interface IBondRegisterRow {
    itemId: string;
    itemCode: string | null;
    itemName: string | null;
    materialType: MaterialType | null;
    uom: string | null;
    importedQty: number;
    consumedQty: number;
    allowedQty: number;
    variance: number;
    overWastage: boolean;
}

export interface IBondRegisterReport {
    buyerOrderId: string;
    rows: IBondRegisterRow[];
    truncated: boolean;
    totalMatchingRows: number;
}

// ---- Order profitability report (T6, brief item 16.3) -----------------------------------------
// Mirrors backend OrderProfitabilityReportDTO verbatim. `marginPercent` is null when `revenueBase`
// is zero (divide-by-zero guard) — render "—", never a fabricated 0/100. `headlineCostBasis` names
// which of receivedCost/committedCost fed grossMargin ("RECEIVED" | "COMMITTED").

export interface IOrderProfitabilityReport {
    buyerOrderId: string;
    orderNumber: string;
    orderCurrencyCode: string;
    exchangeRateApplied: number;
    totalQuantity: number;
    revenueBase: number;
    committedCost: number;
    receivedCost: number;
    plannedCmCost: number;
    plannedCostBase: number;
    headlineCostBasis: 'RECEIVED' | 'COMMITTED' | 'PLANNED' | 'RECEIVED_PLUS_PLANNED' | 'RECEIVED_PLUS_COMMITTED';
    /** max(received, committed, planned): actual for received goods + planned/committed for the unreceived remainder. */
    headlineCost: number;
    unreceivedCost: number;
    grossMargin: number;
    marginPercent: number | null;
}

// ---- Shipping & export docs (15.2/15.3) --------------------------------------------------------
// Mirrors backend com.kervolt.oms.commercial.dataclass.{PackingListDTO,PackingListCartonDTO,
// BillOfLadingDTO,...} verbatim. Packing list is a header + carton child lines (roll-ups are
// computed server-side, recomputed from live cartons on every carton write — never settable
// through the create/update request). Bill of lading is a flat header, no children.

export type BillOfLadingType = 'ON_BOARD' | 'RECEIVED_FOR_SHIPMENT' | 'SEAWAY' | 'HOUSE' | 'MASTER';
export type FreightTerms = 'PREPAID' | 'COLLECT';

/** Packing list header detail (backend PackingListDTO). totalCartons/totalQuantity/
 * totalNetWeight/totalGrossWeight are computed roll-ups — never sent on create/update. */
export interface PackingListDTO {
    id: string;
    institutionId: string;
    packingListNumber: string;
    packingDate: string;
    expFormId?: string;
    buyerOrderId?: string;
    destination?: string;
    totalCartons: number;
    totalQuantity: number;
    totalNetWeight: number;
    totalGrossWeight: number;
    remarks?: string;
}

/** {@code expFormId}/{@code buyerOrderId} are nullable soft references (§4), validated only when
 * supplied. No roll-up fields — those are computed, never settable through the API. */
export interface CreatePackingListRequest {
    packingListNumber: string;
    packingDate: string;
    expFormId?: string;
    buyerOrderId?: string;
    destination?: string;
    remarks?: string;
}

export interface UpdatePackingListRequest extends CreatePackingListRequest {}

/** One carton line detail (backend PackingListCartonDTO). styleId is a nullable soft reference,
 * validated nowhere (free cross-module reference). */
export interface PackingListCartonDTO {
    id: string;
    packingListId: string;
    cartonNo: string;
    description?: string;
    styleId?: string;
    colorName?: string;
    sizeName?: string;
    quantity?: number;
    netWeight?: number;
    grossWeight?: number;
    dimensions?: string;
    sortOrder?: number;
}

export interface CreatePackingListCartonRequest {
    cartonNo: string;
    description?: string;
    styleId?: string;
    colorName?: string;
    sizeName?: string;
    quantity?: number;
    netWeight?: number;
    grossWeight?: number;
    dimensions?: string;
    sortOrder?: number;
}

export interface UpdatePackingListCartonRequest extends CreatePackingListCartonRequest {}

/** Bill of lading / forwarder header detail (backend BillOfLadingDTO). Flat header, no children.
 * forwarderPartyId/carrierPartyId link the free-text forwarder/carrier name fields to the
 * commercial party master (Task 4a, spec §2, 7.4). */
export interface BillOfLadingDTO {
    id: string;
    institutionId: string;
    blNumber: string;
    blDate: string;
    blType?: BillOfLadingType;
    expFormId?: string;
    exportLcId?: string;
    buyerOrderId?: string;
    forwarderName?: string;
    carrierName?: string;
    forwarderPartyId?: string;
    carrierPartyId?: string;
    vesselName?: string;
    voyageNumber?: string;
    containerNumber?: string;
    sealNumber?: string;
    portOfLoading?: string;
    portOfDischarge?: string;
    placeOfDelivery?: string;
    shippingMarks?: string;
    freightTerms?: FreightTerms;
    remarks?: string;
}

/** {@code expFormId}/{@code exportLcId}/{@code buyerOrderId} are nullable soft references (§4),
 * validated only when supplied. {@code blType}/{@code freightTerms} are nullable descriptive enums. */
export interface CreateBillOfLadingRequest {
    blNumber: string;
    blDate: string;
    blType?: BillOfLadingType;
    expFormId?: string;
    exportLcId?: string;
    buyerOrderId?: string;
    forwarderName?: string;
    carrierName?: string;
    forwarderPartyId?: string;
    carrierPartyId?: string;
    vesselName?: string;
    voyageNumber?: string;
    containerNumber?: string;
    sealNumber?: string;
    portOfLoading?: string;
    portOfDischarge?: string;
    placeOfDelivery?: string;
    shippingMarks?: string;
    freightTerms?: FreightTerms;
    remarks?: string;
}

export interface UpdateBillOfLadingRequest extends CreateBillOfLadingRequest {}

// ---- B2B LC accounting (accounting wave 2, lane H) -----------------------------------------------
// Mirrors backend com.kervolt.oms.commercial.dataclass.b2b records.

export type B2bAcceptanceStatus = 'ACCEPTED' | 'PARTIALLY_PAID' | 'PAID';

export interface B2bAcceptancePayment {
    id: string;
    acceptanceId: string;
    paymentDate: string;
    foreignAmount: number;
    paymentRate: number;
    paidBase: number;
    liabilityClearedBase: number;
    /** cleared - paid: positive = realised FX gain, negative = loss. */
    fxGainLossBase: number;
    bankCharges: number;
    paymentAccountId?: string | null;
    reference?: string | null;
    remarks?: string | null;
    journalEntryId?: string | null;
}

export interface B2bAcceptance {
    id: string;
    backToBackLcId: string;
    acceptanceDate: string;
    billReference: string;
    foreignAmount: number;
    acceptanceRate: number;
    baseAmount: number;
    usanceDays: number;
    maturityDate: string;
    status: B2bAcceptanceStatus;
    paidForeignAmount: number;
    outstandingForeignAmount: number;
    liabilityClearedBase: number;
    outstandingBase: number;
    overdue: boolean;
    remarks?: string | null;
    journalEntryId?: string | null;
    payments: B2bAcceptancePayment[];
    /** Set only on the create response: advisory notes (default GRNI clearing, open supplier bills). */
    warnings?: string[];
}

export interface B2bMargin {
    marginPercent?: number | null;
    marginAmount?: number | null;
    marginDate?: string | null;
    marginBankAccountId?: string | null;
    marginJournalEntryId?: string | null;
    marginReleaseDate?: string | null;
    marginReleaseJournalEntryId?: string | null;
}

export interface B2bLcAccounting {
    backToBackLcId: string;
    currencyCode: string;
    margin: B2bMargin;
    acceptances: B2bAcceptance[];
    acceptedForeign: number;
    paidForeign: number;
    outstandingForeign: number;
    outstandingBase: number;
}

export interface CreateB2bAcceptanceRequest {
    acceptanceDate: string;
    billReference: string;
    foreignAmount: number;
    acceptanceRate: number;
    usanceDays: number;
    remarks?: string;
}

export interface CreateB2bAcceptancePaymentRequest {
    paymentDate: string;
    foreignAmount: number;
    paymentRate: number;
    bankCharges?: number;
    paymentAccountId?: string | null;
    reference?: string;
    remarks?: string;
}

export interface SetB2bMarginRequest {
    marginPercent?: number | null;
    marginAmount: number;
    marginDate: string;
    bankAccountId?: string | null;
}

export type B2bMaturityBucket = 'OVERDUE' | '0-30' | '31-60' | '61-90' | '90+';

export interface B2bMaturityRow {
    acceptanceId: string;
    backToBackLcId: string;
    masterLcId: string;
    b2bLcNumber: string;
    issuingBank: string;
    supplierName: string;
    currencyCode: string;
    billReference: string;
    maturityDate: string;
    daysToMaturity: number;
    bucket: B2bMaturityBucket;
    outstandingForeign: number;
    outstandingBase: number;
    /** W3-B5: past maturity with an amount still owing: can be retired by a PAD / time loan. */
    padCandidate?: boolean;
}

export interface B2bMaturityTotal {
    key: string;
    count: number;
    foreignByCurrency: Record<string, number>;
    outstandingBase: number;
}

export interface B2bMaturitySchedule {
    asOf: string;
    rows: B2bMaturityRow[];
    byBucket: B2bMaturityTotal[];
    byBank: B2bMaturityTotal[];
    bySupplier: B2bMaturityTotal[];
    totalOutstandingBase: number;
    overdueOutstandingBase: number;
}
